import React, { useState } from 'react'
import { bool, node, shape, string } from 'prop-types'
import { PageHead } from '@fs/zion-head'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Paragraph, Button, useContainerWidth, BillboardButton, H1, Separator } from '@fs/zion-ui'
import ZionForm, { FormTextField, FormAutosuggest, useZionForm } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import { css } from '@emotion/core'
import { getAnonSession } from '@fs/zion-session'
import getCanonicalUrl from '../../helpers/canonicalUrl'
import { useRegionLanguage, useCorrections } from '../../../aam/AppAam/hooks/hooks'
import { useDSCDispatch, ACTIONS } from '../../../state/DSCStore'
import wifImage from './assets/WIF.jpg'
import wifIcon from './assets/wifIcon.svg'
import { setSessionStorageItem } from '../../helpers/sessionStorage'
import ConditionalHeader from '../ConditionalHeader/ConditionalHeader'
import sanitizeInputToDisplayName from '../../helpers/sanitize_input'

export default function LandingPage(props) {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const DSCDispatch = useDSCDispatch()
  const isCorrections = useCorrections()
  const [toggleGuestForm, setToggleGuestForm] = useState(!!isCorrections)
  const countryLanguageOptions = useRegionLanguage(t)
  const { guestFields } = props
  const guestFieldsForForm = guestFields ?? {}
  const {
    splashImage = wifImage,
    splashImageAlt = t('dlp.wiflandingpage.mainimage', 'where am I from - family looking at photos together'),
    icon = wifIcon,
    // The logo is the only thing naming the experience in the banner, so it is
    // meaningful, not decorative - each caller that swaps `icon` must swap this
    // with it or the alt text describes the wrong experience (WCAG 1.1.1, DSC-10944).
    iconAlt = t('dlp.wiflandingpage.logo', 'Where am I from logo'),
    appName = 'Where am I From?: Sign Up',
    headerText = t('wif.landingpage.title', 'Where Am I From?'),
    buttonColor = '#026184',
    showAccountCreationText = false,
    signInHref,
    children,
    showSignIn = true,
    guestTo = '',
    title = t('landing-page.wcf.page.title', "Where Am I From? It's More Than Just a Map—It's Your Legacy"),
    description = t(
      'landing-page.wcf.page.description',
      'Find out where your story really began—explore the places, cultures, and traditions that shaped your ancestors (and helped make you you).'
    ),
  } = props

  setSessionStorageItem({ guestTree: [] })

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? `${Number.parseInt(result[1], 16)},${Number.parseInt(result[2], 16)},${Number.parseInt(result[3], 16)}`
      : null
  }

  // Awful nasty hack to prevent last name from zooming on Safari
  // https://stackoverflow.com/questions/2989263/disable-auto-zoom-in-input-text-tag-safari-on-iphone/6394497#6394497
  const textFieldNoZoomStyle = css`
    margin-top: 25px;
    input {
      font-size: 16px;
    }
  `

  const createAccountCSS = css`
    max-width: 570px;
  `
  const mainCSS = css`
    display: flex;
    flex-direction: column;
  `
  const imgCSS = css`
    width: 100%;
    max-height: 400px;
    object-fit: cover;
  `
  const iconAndTitleWrapperCSS = css`
    margin: 1vw;
    display: flex;
    color: white;
    position: absolute;
    height: 100%;
    font-family: 'museo', Verdana, Ayuthaya, 'HanaMinBFont', serif;
  `
  const bannerSectionCSS = css`
    position: relative;
  `
  const iconCSS = css`
    position: relative;
    top: 61%;
    inset-inline-start: 18%;
    height: 25%;
  `
  const headerCSS = css`
    display: ${atWidth({ sm: 'block', lg: 'block' })};
    ${atWidth({ default: 'margin-top:24px;', sm: 'margin-top:24px;' })}
  `
  const contentCSS = css`
    padding-inline-start: 3.3%;
    padding-inline-end: ${atWidth({ default: '10px;', md: '20%;' })};
  `
  const buttonsCSS = css`
    display: flex;
    align-items: center;
    margin-top: 25px;
    margin-bottom: 5px;
    [emphasis='billboard'] {
      background: ${buttonColor} !important;
      color: white;
    }
    [emphasis='billboard']:hover {
      background: rgba(${hexToRgb(buttonColor)}, 0.7) !important;
    }
    [emphasis='billboard']:disabled {
      background: #ddd !important;
    }
    > * {
      margin-inline-end: 20px;
    }
  `
  const validationSchema = Yup.object().shape({
    firstName: guestFieldsForForm.firstName
      ? Yup.string().trim().required(t('aam-landing-validation-enter-first-name', 'Please enter your first name'))
      : Yup.mixed().nullable(),
    lastName: guestFieldsForForm.lastName
      ? Yup.string().trim().required(t('aam-landing-validation-enter-last-name', 'Please enter your last name'))
      : Yup.mixed().nullable(),
    languageRegion: guestFieldsForForm.languageRegion
      ? Yup.object().required(
          t('aam-landing-validation-select-country-language', 'Please select a country and language')
        )
      : Yup.mixed().nullable(),
    birthYear: guestFieldsForForm.birthYear
      ? Yup.number()
          .typeError(t('aam-landing-validation-enter-birthyear', 'Please enter your birth year'))
          .required(t('aam-landing-validation-enter-birthyear', 'Please enter your birth year'))
          .min(1901, t('aam-landing-validation-enter-birthyear-after-1900', 'Please enter a birth year after 1900'))
          .max(
            new Date().getFullYear(),
            t('aam-landing-validation-enter-valid-birthyear', 'Please enter a valid birth year')
          )
      : Yup.mixed().nullable(),
  })

  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      birthYear: '',
      languageRegion: undefined,
    },
  })

  const {
    reset,
    formState: { isValid },
  } = methods

  const dispatchToGlobal = (values) => {
    getAnonSession().then(() => {
      DSCDispatch({
        type: ACTIONS.POPULATE_NONSTANDARD_USER,
        data: { nonstandardUser: { ...values } },
      })
    })
  }

  const submitAnalytics = (countryLang) => {
    if (appName === 'All About Me' && countryLang) {
      trackEvent({
        event_name: `${appName}:GUEST:SUBMIT`,
        allAboutMe_countryLanguage: countryLang,
      })
    } else trackEvent({ event_name: appName })
  }

  const sanitizeValues = (values) => {
    const sanitizedValues = { ...values }
    if (guestFieldsForForm.firstName) {
      sanitizedValues.firstName = sanitizeInputToDisplayName(values.firstName)
    }
    if (guestFieldsForForm.lastName) {
      sanitizedValues.lastName = sanitizeInputToDisplayName(values.lastName)
    }
    return sanitizedValues
  }

  const passToGlobal = (values) => {
    reset()
    const sanitizedValues = sanitizeValues(values)
    submitAnalytics(sanitizedValues.languageRegion)
    dispatchToGlobal(sanitizedValues)
  }

  const closeGuestForm = () => {
    reset()
    setToggleGuestForm(false)
  }

  // Only pass `to` when a guest destination is configured. A zion-ui Button
  // update made any `to` value (including the default empty string) render the
  // button as a link, which hijacked the click and broke the anonymous guest
  // flow — sending walk-in/kiosk guests to login instead. See DSC-9042.
  const guestButtonProps = guestTo ? { to: guestTo } : {}

  const suppressAutocomplete = (event) => {
    event.target.setAttribute('autocomplete', 'disabled')
  }

  return (
    <div css={mainCSS}>
      <ConditionalHeader />
      <PageHead omitSiteName description={description} title={title} url={getCanonicalUrl()} />
      <div css={bannerSectionCSS}>
        <div css={iconAndTitleWrapperCSS}>
          <img src={icon} css={iconCSS} alt={iconAlt} />
        </div>
        {!isCorrections && <img src={splashImage} css={imgCSS} alt={splashImageAlt} />}
      </div>
      <div css={contentCSS}>
        <div css={headerCSS}>
          <H1 size="H3">{headerText}</H1>
          <Paragraph>{description}</Paragraph>
        </div>
        <div css={buttonsCSS}>
          {showSignIn && !isCorrections && (
            <>
              <BillboardButton
                data-testid="landing-page-login-billboard-button"
                to={signInHref}
                linkName="loggedOutPage:LOGIN:CLICK"
                external
              >
                {t('dlp.wiflandingpage.signin', 'Sign In')}
              </BillboardButton>

              {(guestFields || isCorrections) && (
                <Button
                  {...guestButtonProps}
                  data-testid="Guest Login"
                  onClick={() => {
                    trackEvent({ event_name: `DS_${appName}:CONTINUE_AS_GUEST:CLICK` })
                    const hasGuestFields = Object.keys(guestFieldsForForm).length > 0
                    return hasGuestFields ? setToggleGuestForm(!toggleGuestForm) : dispatchToGlobal()
                  }}
                >
                  {t('dlp.landingpagecomponent.guest', 'Continue as guest')}
                </Button>
              )}
            </>
          )}
        </div>
        {toggleGuestForm && (
          <>
            {showAccountCreationText && (
              <div css={createAccountCSS}>
                <Paragraph size="sm">
                  {t(
                    'dlp.wiflandingpage.underbuttonsubtext',
                    `Don't have an account? No problem. Creating an account is free and easy. Before using the experience, try to enter at least four generations of information in your family tree.`
                  )}
                </Paragraph>
              </div>
            )}

            <ZionForm methods={methods} onSubmit={passToGlobal}>
              <div css={textFieldNoZoomStyle}>
                {guestFieldsForForm.firstName && (
                  <FormTextField
                    data-testid="landing-page-first-name-text-field"
                    name="firstName"
                    placeholder={t('dlp.landingpage.firstname', 'Your first name')}
                    label={t('dlp.landingpage.firstname', 'Your first name')}
                  />
                )}
                {guestFieldsForForm.lastName && (
                  <FormTextField
                    data-testid="landing-page-last-name-text-field"
                    name="lastName"
                    placeholder={t('dlp.landingpage.lastname', 'Your last name')}
                    label={t('dlp.landingpage.lastname', 'Your last name')}
                  />
                )}
                {guestFieldsForForm.birthYear && (
                  <FormTextField
                    data-testid="landing-page-birth-year-text-field"
                    name="birthYear"
                    placeholder={t('dlp.landingpage.birthyear', 'Birth Year')}
                    label={t('dlp.landingpage.birthyear', 'Birth Year')}
                    type="number"
                    helpMessage=""
                  />
                )}
                {guestFieldsForForm.languageRegion && (
                  <FormAutosuggest
                    suggestions={countryLanguageOptions}
                    name="languageRegion"
                    inputProps={{
                      onFocus: suppressAutocomplete,
                    }}
                    label={t('aam-landing-validation-select-country-language')}
                    placeholder={t('aam-landing-validation-select-country-language')}
                    aria-label={t('aam-landing-validation-select-country-language-aria')}
                  />
                )}
                <div css={buttonsCSS}>
                  <BillboardButton
                    data-testid="landing-page-submit-billboard-button"
                    type="submit"
                    external="true"
                    disabled={!isValid}
                  >
                    {t('common-ui:submit.action', 'Submit')}
                  </BillboardButton>
                  {!isCorrections && (
                    <Button data-testid="landing-page-button" onClick={closeGuestForm}>
                      {' '}
                      {t('common-ui:cancel.action', 'Cancel')}
                    </Button>
                  )}
                </div>
              </div>
            </ZionForm>
          </>
        )}
        {children}
      </div>
      <Separator size="xxl" />
    </div>
  )
}

LandingPage.propTypes = {
  guestFields: shape({
    firstName: bool,
    lastName: bool,
    birthYear: bool,
    languageRegion: bool,
  }),
  splashImage: string,
  splashImageAlt: string,
  icon: string,
  iconAlt: string,
  appName: string,
  headerText: string,
  buttonColor: string,
  showAccountCreationText: bool,
  signInHref: string,
  children: node,
  showSignIn: bool,
  guestTo: string,
  title: string,
  description: string,
}
